<template>
  <a-card class="quote-costs">
    <v-col>
      <div class="quote-costs-title">Program</div>
      <div
        v-for="(day, dayIndex) in data.costs"
        :key="dayIndex"
        class="quote-day-block"
        :class="{ 'quote-day-block--alt': dayIndex % 2 === 1 }"
      >
        <div class="quote-day-header">
          <div class="quote-day-reorder">
            <v-btn icon x-small :disabled="readonly" @click="reorderDayDown(dayIndex)">
              <v-icon small>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn icon x-small :disabled="readonly" @click="reorderDayUp(dayIndex)">
              <v-icon small>mdi-arrow-up</v-icon>
            </v-btn>
          </div>
          <div class="quote-day-title">{{ formatDayWithDate(data.period, dayIndex) }}</div>
        </div>

        <div class="quote-day-route-bar">
          <div class="quote-day-field" :class="{ 'quote-day-field--filled': hasCityValue(day.cityStart) }">
            <f-dropdown
              hideClear
              placeholder="City start"
              :items="getCityItems(day)"
              :value="day.cityStart"
              :disabled="readonly"
              @input="onCityStartChanged(day, $event)"
            />
          </div>

          <div class="quote-day-connector" aria-hidden="true">→</div>

          <div class="quote-day-field">
            <div class="quote-day-field-row" :class="{ 'quote-day-field--filled': hasCityValue(day.cityEnd) }">
              <f-dropdown
                hideClear
                placeholder="City end"
                :items="getCityItems(day)"
                :value="day.cityEnd"
                :disabled="readonly"
                @input="onCityEndChanged(day, $event)"
              />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="quote-day-sync"
                    :disabled="readonly"
                    v-bind="attrs"
                    v-on="on"
                    @click="syncCitiesFromItems(dayIndex)"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Sync cities from products</span>
              </v-tooltip>
            </div>
          </div>

          <div class="quote-day-field quote-day-field--via">
            <f-text
              :value="day.routeId ? getSelectedRouteLabel(day.routeId) : ''"
              placeholder="Vias"
              disabled
            />
          </div>

          <div class="quote-day-field quote-day-field--action">
            <v-btn
              x-small
              outlined
              color="primary"
              class="quote-day-route-btn"
              :disabled="readonly || !day.cityStart || !day.cityEnd"
              @click="openRouteDialog(dayIndex)"
            >
              <v-icon left x-small>mdi-map-marker-path</v-icon>
              {{ day.routeId ? "Change route" : "Select route" }}
            </v-btn>
          </div>

          <div class="quote-day-meals">
            <span class="quote-day-meals-label">Meals :</span>
            <div class="quote-day-meals-options">
              <div class="quote-day-meal-item">
                <span class="quote-day-meal-letter" @click="!readonly && (day.breakfast = !day.breakfast)">B</span>
                <v-checkbox
                  v-model="day.breakfast"
                  dense
                  hide-details
                  class="quote-day-meal-check"
                  :disabled="readonly"
                />
              </div>
              <div class="quote-day-meal-item">
                <span class="quote-day-meal-letter" @click="!readonly && (day.lunch = !day.lunch)">L</span>
                <v-checkbox
                  v-model="day.lunch"
                  dense
                  hide-details
                  class="quote-day-meal-check"
                  :disabled="readonly"
                />
              </div>
              <div class="quote-day-meal-item">
                <span class="quote-day-meal-letter" @click="!readonly && (day.dinner = !day.dinner)">D</span>
                <v-checkbox
                  v-model="day.dinner"
                  dense
                  hide-details
                  class="quote-day-meal-check"
                  :disabled="readonly"
                />
              </div>
            </div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon x-small color="grey" class="quote-day-meals-info" v-bind="attrs" v-on="on">
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>B = Breakfast, L = Lunch, D = Dinner</span>
            </v-tooltip>
          </div>
        </div>

        <div class="quote-day-products">
          <table class="quote-day-table">
            <thead>
              <tr>
                <th class="quote-day-table__reorder"></th>
                <th>City</th>
                <th>Supplier</th>
                <th>Product</th>
                <th>Type</th>
                <th>Cost type</th>
                <th>Cost</th>
                <th class="quote-day-table__icons" colspan="5"></th>
                <th class="quote-day-table__program">Show in program</th>
                <th class="quote-day-table__actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="day.items.length === 0" class="quote-day-table__empty-row">
                <td colspan="14">
                  <span class="quote-day-empty-label">No items added yet</span>
                </td>
              </tr>
              <tr
                v-for="(item, itemIndex) in day.items"
                :key="itemIndex"
                class="quote-day-table__row quote-product-type-row"
                :style="getProductTypeCssVars(item.productType)"
              >
                <td class="quote-day-table__reorder" align="center">
                  <v-btn icon x-small :disabled="readonly" @click="reorderItemDown(dayIndex, itemIndex)">
                    <v-icon small>mdi-arrow-down</v-icon>
                  </v-btn>
                  <v-btn icon x-small :disabled="readonly" @click="reorderItemUp(dayIndex, itemIndex)">
                    <v-icon small>mdi-arrow-up</v-icon>
                  </v-btn>
                </td>
              <td>
                <f-text v-model="item.supplierCity" smlabel :disabled="readonly || isCityDisabled(dayIndex, itemIndex)" />
              </td>
                <td>
                  <div class="quote-day-inline-field">
                    <autocomplete-grouped-free-text
                      :value="item.supplier"
                      :items="
                        getSupplierItems(data.period, item.productType, getSupplierContextKey(item.uuid), item.supplier)
                      "
                      :loading="isSupplierLoading(getSupplierContextKey(item.uuid))"
                      :has-more="hasMoreSuppliers(getSupplierContextKey(item.uuid))"
                      class="flex-grow-1"
                      :disabled="readonly"
                      @input="onSupplierChanged(dayIndex, itemIndex, $event, item.productType)"
                      @focus="onSupplierFocus(item.uuid, item.productType)"
                      @search="onSupplierSearch(item.uuid, item.productType, $event)"
                      @load-more="onSupplierLoadMore(item.uuid, item.productType)"
                    />
                    <v-btn
                      icon
                      x-small
                      @click="redirectToSupplier(item.supplier)"
                      :disabled="readonly || item.supplier?.value == null"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <div class="quote-day-inline-field">
                    <autocomplete-grouped-free-text
                      :value="item.product"
                      :items="getProductItemsBySupplier(item.supplier, item.productType, data.period, dayIndex)"
                      :disabled="readonly || isProductNameDisabled(dayIndex, itemIndex)"
                      class="flex-grow-1"
                      @input="onProductChanged(data.period, dayIndex, itemIndex, $event)"
                      @focus="onProductFocus(item.supplier)"
                    />
                    <v-btn icon x-small @click="redirectToProduct(item.product)" :disabled="readonly || item.product?.value == null">
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <f-dropdown
                    hideClear
                    :value="item.productType"
                    :items="getProductTypes"
                    :disabled="readonly || isProductTypeDisabled(dayIndex, itemIndex)"
                    @input="onProductTypeChanged(dayIndex, itemIndex, $event, item.supplier)"
                    class="quote-day-type-field"
                  />
                </td>
                <td>
                  <f-dropdown
                    hideClear
                    :value="item.costType"
                    :items="getCostTypes"
                    :disabled="readonly || isProductTypeDisabled(dayIndex, itemIndex)"
                    @input="onCostTypeChanged(dayIndex, itemIndex, $event)"
                    class="quote-day-cost-type-field"
                  />
                </td>
              <td>
                <div
                  class="d-flex justify-center align-center"
                  :class="{ 'quote-cost-field--missing': isProductCostMissing(item) }"
                >
                  <f-number
                    v-model="item.productCost.value.default"
                    smlabel
                    :disabled="isPriceFieldDisabled(dayIndex, itemIndex)"
                    @blur="onCostBlur(item)"
                  />
                </div>
              </td>
                <td class="quote-day-table__icon-cell">
                  <v-tooltip top v-if="hasBreakfastIncluded(item)">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" small color="grey lighten-1">free_breakfast</v-icon>
                    </template>
                    <span>Breakfast Included</span>
                  </v-tooltip>
                </td>
                <td class="quote-day-table__icon-cell">
                  <v-tooltip top v-if="item.productCost.valueIncludesPorterage">
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on">mdi-bag-carry-on</v-icon>
                    </template>
                    <span>Porterage Included</span>
                  </v-tooltip>
                </td>
                <td class="quote-day-table__icon-cell">
                  <v-tooltip top v-if="item.productCost.confirm">
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on" :color="item.productCost.rateKind === 'CONTRACT' ? 'green' : ''">
                        mdi-check-circle-outline
                      </v-icon>
                    </template>
                    <span>Confirmed Rate</span>
                  </v-tooltip>
                </td>
                <td class="quote-day-table__icon-cell">
                  <v-tooltip top v-if="isSeasonalCost(item.productCost.type)">
                    <template v-slot:activator="{ on }">
                      <v-btn color="grey" v-on="on" icon x-small>
                        <img src="@/assets/img/warning.png" class="quote-day-warning-icon" />
                      </v-btn>
                    </template>
                    <span>{{ seasonalPriceStartMessage }}</span>
                  </v-tooltip>
                </td>
                <td class="quote-day-table__icon-cell">
                  <v-btn
                    icon
                    x-small
                    :disabled="isLockButtonDisabled(dayIndex, itemIndex)"
                    @click="updateEditableCostStatus(data.period, dayIndex, itemIndex)"
                  >
                    <v-icon small>{{ getEditableCostIconButton(dayIndex, itemIndex) }}</v-icon>
                  </v-btn>
                </td>
                <td class="quote-day-table__program">
                  <div class="quote-program-toggle">
                    <v-switch v-model="item.isShowInProgram" dense hide-details :disabled="readonly" />
                  </div>
                </td>
                <td class="quote-day-table__actions" align="center">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-bind="attrs" v-on="on" aria-label="Actions" :disabled="readonly">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="duplicateCostToNextDay(dayIndex, itemIndex)">
                        <v-icon left small>mdi-content-copy</v-icon>
                        <v-list-item-title>Duplicate On Next Day</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        :disabled="item.isProductEditableCost"
                        @click="refreshProductCostType(data.period, dayIndex, itemIndex)"
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        <v-list-item-title>Reload Cost</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="removeCost(dayIndex, itemIndex)">
                        <v-icon left small>clear</v-icon>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
              <tr class="quote-day-table__footer">
                <td colspan="14">
                  <v-btn small text color="primary" class="quote-day-add-line" :disabled="readonly" @click="addCost(dayIndex)">
                    <v-icon left small>mdi-plus</v-icon>
                    Add new line
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </v-col>
    <QuoteRouteDialog v-if="routeDialog" v-model="routeDialog" @select="onRouteSelect" />
  </a-card>
</template>

<script>
import { ProductMenu } from "@/config/menus";
import quoteUtils from "@/modules/quotes/components/utils";
import { isProductCostMissing } from "@/modules/quotes/components/quoteValidation";
import { getQuoteProductTypeCssVars } from "@/modules/quotes/components/quoteProductTypeColors";
import money from "@/services/money";
import MONTHS from "@/config/MONTHS";
import QuoteRouteDialog from "@/modules/quotes/components/program/QuoteRouteDialog";
import { normalizeRouteVias } from "@/modules/routes";
import {
  emitSupplierDropdownEvent,
  getSupplierContextKey,
  getSupplierDropdownEntry,
  getSupplierEntriesByContext,
  hasMoreSuppliers,
  isSupplierLoading,
  mapGroupedSupplierItems,
} from "@/modules/quotes/components/supplierDropdown";
export default {
  components: {
    QuoteRouteDialog,
  },
  props: {
    value: Object,
    supplierProductData: Object,
    readonly: {
      type: Boolean,
      default: false,
    },
    allowPriceEdits: {
      type: Boolean,
      default: false,
    },
    supplierDropdownState: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      cities: [],
      routeDialog: null,
      selectedRoutesById: {},
    };
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    isPriceInteractionLocked() {
      return this.readonly && !this.allowPriceEdits;
    },
    getProductTypes() {
      return ProductMenu.map((product) => ({ label: product.title, value: product.module }));
    },
    getCostTypes() {
      return [
        { label: "Per Person", value: "PP" },
        { label: "Per Day", value: "PD" },
        { label: "Per Group", value: "PG" },
      ];
    },
    seasonalPriceStartMessage() {
      const { startMonth, startDay } = this.data.period;
      const month = startMonth ?? 1;
      const day = startDay ?? 1;
      return `It uses the seasonal price from ${MONTHS[month - 1].label} ${day}st`;
    },
    environment() {
      return this.$store.getters["AccountStore/environment"];
    },
    currentCurrency() {
      return money.getCurrencySource(this.environment).currency;
    },
  },
  methods: {
    getProductTypeCssVars(productType) {
      return getQuoteProductTypeCssVars(productType);
    },
    isProductCostMissing,
    addCost(dayIndex) {
      if (this.readonly) return;
      this.data.costs[dayIndex].items.push({
        uuid: quoteUtils.generateUUID(),
        productCost: {
          costType: "custom",
          currency: this.currentCurrency,
          value: {
            default: 0,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        },
        costType: null,
        isProductEditableCost: true,
        isShowInProgram: true,
        logisticsCostUUIDs: [],
      });
    },
    onCostBlur(item) {
      if (this.isPriceInteractionLocked) return;
      item.productCost.value.default = quoteUtils.roundToTwoDecimals(item.productCost.value.default);
      if (this.allowPriceEdits) this.$emit("price-override-changed");
    },
    duplicateCostToNextDay(dayIndex, itemIndex) {
      if (this.readonly) return;
      const { items } = this.data.costs[dayIndex];
      if (dayIndex + 1 < this.data.costs.length) {
        const copyData = { ...items[itemIndex], uuid: quoteUtils.generateUUID() };
        this.data.costs[dayIndex + 1].items.push(copyData);
      }
    },
    removeCost(dayIndex, itemIndex) {
      if (this.readonly) return;
      this.data.costs[dayIndex].items.splice(itemIndex, 1);
    },
    reorderDayDown(dayIndex) {
      if (this.readonly) return;
      const nextDayIndex = dayIndex + 1;
      if (nextDayIndex >= this.data.costs.length) return;

      this.swapDays(dayIndex, nextDayIndex);
    },
    reorderDayUp(dayIndex) {
      if (this.readonly) return;
      const nextPreviousIndex = dayIndex - 1;
      if (nextPreviousIndex < 0) return;

      this.swapDays(dayIndex, nextPreviousIndex);
    },
    reorderItemDown(dayIndex, itemIndex) {
      if (this.readonly) return;
      const nextItemIndex = itemIndex + 1;
      if (nextItemIndex >= this.data.costs[dayIndex].items.length) return;

      this.swapItems(dayIndex, itemIndex, nextItemIndex);
    },
    reorderItemUp(dayIndex, itemIndex) {
      if (this.readonly) return;
      const previousItemIndex = itemIndex - 1;
      if (previousItemIndex < 0) return;

      this.swapItems(dayIndex, itemIndex, previousItemIndex);
    },
    swapDays(dayIndex1, dayIndex2) {
      const costs = [...this.data.costs];
      const temp = costs[dayIndex1];
      costs[dayIndex1] = costs[dayIndex2];
      costs[dayIndex2] = temp;

      this.data = { ...this.data, costs };
    },
    swapItems(dayIndex, itemIndex1, itemIndex2) {
      const costs = [...this.data.costs];
      const items = [...costs[dayIndex].items];
      const temp = items[itemIndex1];
      items[itemIndex1] = items[itemIndex2];
      items[itemIndex2] = temp;

      costs[dayIndex] = { ...costs[dayIndex], items };
      this.data = { ...this.data, costs };
    },
    isCityDisabled(dayIndex, itemIndex) {
      const supplier = this.data.costs[dayIndex]?.items[itemIndex]?.supplier;
      if (quoteUtils.isTBDSupplier(supplier?.value)) return false;
      return !supplier || !supplier.label || supplier.value != null;
    },
    isProductNameDisabled(dayIndex, itemIndex) {
      return this.data.costs[dayIndex]?.items[itemIndex]?.supplier == null;
    },
    isProductTypeDisabled(dayIndex, itemIndex) {
      return this.data.costs[dayIndex].items[itemIndex].product?.value != null;
    },
    isCostDisabled(dayIndex, itemIndex) {
      return !this.data.costs[dayIndex].items[itemIndex].isProductEditableCost;
    },
    hasBreakfastIncluded(item) {
      if (!item || item.productType !== "ROOM") return false;
      const supplierId = item.supplier?.value;
      if (supplierId == null) return false;
      const breakfast = this.supplierProductData?.[supplierId]?.supplier?.meta?.breakfast;
      return breakfast?.value === "included";
    },
    isPriceFieldDisabled(dayIndex, itemIndex) {
      return this.isPriceInteractionLocked || this.isCostDisabled(dayIndex, itemIndex);
    },
    isProductEditableCostDisabled(dayIndex, itemIndex) {
      return this.data.costs[dayIndex].items[itemIndex].product?.value == null;
    },
    isLockButtonDisabled(dayIndex, itemIndex) {
      return this.isPriceInteractionLocked || this.isProductEditableCostDisabled(dayIndex, itemIndex);
    },
    getEditableCostIconButton(dayIndex, itemIndex) {
      return this.data.costs[dayIndex].items[itemIndex].isProductEditableCost
        ? "mdi-lock-outline"
        : "mdi-lock-off-outline";
    },
    updateEditableCostStatus(period, dayIndex, itemIndex) {
      if (this.isPriceInteractionLocked) return;
      const item = this.data.costs[dayIndex].items[itemIndex];
      item.isProductEditableCost = !item.isProductEditableCost;

      if (item.isProductEditableCost) {
        item.productCost = {
          type: "custom",
          currency: this.currentCurrency,
          value: {
            default: item.productCost.value.default,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        };
        if (this.allowPriceEdits) this.$emit("price-override-changed");
        return;
      }

      const supplierId = item.supplier.value;
      const productId = item.product.value;

      if (!supplierId || !productId) {
        if (this.allowPriceEdits) this.$emit("price-override-changed");
        return;
      }

      const product = this.supplierProductData[supplierId].products[productId];
      const previousDayHotelId = this.getPreviousDayHotelId(dayIndex);
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        dayIndex,
        previousDayHotelId
      );

      item.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      item.productType = product.product_type;
      item.costType = productCost.costType;
      this.applyProgramFields(item, product, supplierId, period);
      quoteUtils.applyShowInProgramForProductType(item);
      if (this.allowPriceEdits) this.$emit("price-override-changed");
    },
    formatDayWithDate(period, index) {
      const { startYear, startMonth, startDay } = period;

      if (startYear == null || startMonth == null || startDay == null) {
        return "Day " + (index + 1);
      }

      const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + index);

      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      const formattedDate = endDate.toLocaleDateString("en-US", options);

      return `Day ${index + 1} ${formattedDate}`;
    },
    getSupplierContextKey(itemUuid) {
      return getSupplierContextKey("cost", itemUuid);
    },
    getSupplierDropdownEntry(contextKey) {
      return getSupplierDropdownEntry(this.supplierDropdownState, contextKey);
    },
    isSupplierLoading(contextKey) {
      return isSupplierLoading(this.supplierDropdownState, contextKey);
    },
    hasMoreSuppliers(contextKey) {
      return hasMoreSuppliers(this.supplierDropdownState, contextKey);
    },
    getSupplierItems(period, productType, contextKey, selectedSupplier) {
      const supplierType = quoteUtils.getSupplierTypeByProductType(productType);
      const dropdownState = this.getSupplierDropdownEntry(contextKey);
      const suppliers = getSupplierEntriesByContext(this.supplierProductData, dropdownState, selectedSupplier);

      const filtered = suppliers.filter(({ supplier }) => {
        const { active, supplier_type, meta, search_types, product_rates } = supplier;

        // If the supplier is not active then skip it
        if (!active) return false;

        // If the user fill product type field then filter by supplier type
        if (supplierType) {
          const supplierTypes = [supplier_type, ...(search_types || [])];
          if (!supplierTypes.includes(supplierType)) return false;
        }

        // If the supplier type is HOTEL or RESTAURANT then filter by hotel class
        if (supplier_type === "HOTEL") {
          const hotelClass = meta?.hotel_class;
          if (hotelClass == null) return false;
          if (!this.data.accommodations.includes(hotelClass)) return false;
        }

        // ** If the product_rates is not available then still include it
        if (!product_rates) return true;

        // Pick the rate for the current year or the latest available year if current year is not available
        const rateYear = Number(period.startYear);
        const fallbackRateYear = Math.max(...Object.keys(product_rates).map(Number));
        const rate = product_rates[rateYear] ?? product_rates[fallbackRateYear];

        // ** If the rate is not available then still include it
        if (!rate) return true;

        // If rate are not includes these codes then skip it
        if (!["PP", "PROOM", "PG", "PD"].some((code) => rate.includes(code))) return false;

        return true;
      });

      return mapGroupedSupplierItems(filtered, { includeCommission: true });
    },
    getProductItemsBySupplier(supplier, productType, period, dayIndex) {
      if (supplier == null) return [];
      if (supplier.value == null) return [];
      const supplierData = this.supplierProductData[supplier.value];
      if (!supplierData || !supplierData.products) return [];

      const products = supplierData.products;

      const filtered = Object.values(products).filter((product) => {
        const { active, product_type } = product;

        // If the product is not active then skip it
        if (!active) return false;

        // If the user fill product type field then filter by product type
        if (productType && productType != product_type) return false;

        return true;
      });

      // Group by product_type
      const grouped = {};
      filtered.forEach((product) => {
        if (!grouped[product.product_type]) {
          grouped[product.product_type] = [];
        }
        grouped[product.product_type].push({
          label: product.name,
          value: product.id,
        });
      });

      // Flatten with headers
      const result = [];
      Object.entries(grouped).forEach(([type, items]) => {
        result.push({ header: type });
        result.push(...items);
      });

      return result;
    },
    onSupplierChanged(dayIndex, itemIndex, v, productType) {
      if (this.readonly) return;
      const newCosts = this.data.costs;
      const item = newCosts[dayIndex].items[itemIndex];

      // Clear product, productCost and isProductEditableCost
      item.product = null;
      item.productCost = {
        type: "custom",
        currency: this.currentCurrency,
        value: {
          default: 0,
          single: null,
          twin: null,
          triple: null,
          quad: null,
        },
        confirm: false,
      };
      item.isProductEditableCost = true;
      item.productProgramDescription = "";
      item.productProgramTitle = "";
      item.programSupplierName = "";

      // Clear supplier and supplierCity if supplier is freetext
      if (v.label === "" && v.value === null) {
        item.supplierCity = "";
        item.supplier = null;
        this.data = { ...this.data, costs: newCosts };
        return;
      }

      item.supplier = v;

      // Show city name if supplier is not freetext
      if (v.value !== null) {
        const supplierData = this.supplierProductData[v.value];
        if (supplierData && supplierData.supplier) {
          item.supplierCity = supplierData.supplier.city ?? "";
          item.programSupplierName = quoteUtils.getProgramSupplierName(supplierData.supplier);
          this.$emit("onSupplierChanged", v.value, productType);
        }
      }

      this.data = { ...this.data, costs: newCosts };
    },
    onSupplierFocus(itemUuid, productType) {
      emitSupplierDropdownEvent(this, "onSupplierFocus", {
        source: "program",
        prefix: "cost",
        itemUuid,
        productType,
      });
    },
    onSupplierSearch(itemUuid, productType, keyword) {
      emitSupplierDropdownEvent(this, "onSupplierSearch", {
        source: "program",
        prefix: "cost",
        itemUuid,
        productType,
        keyword,
      });
    },
    onSupplierLoadMore(itemUuid, productType) {
      emitSupplierDropdownEvent(this, "onSupplierLoadMore", {
        source: "program",
        prefix: "cost",
        itemUuid,
        productType,
      });
    },
    applyProgramFields(item, product, supplierId, period) {
      item.productProgramDescription = quoteUtils.getProductProgramDescription(product, period.startYear);
      item.productProgramTitle = quoteUtils.getProductProgramTitle(product, period.startYear);
      item.programSupplierName = quoteUtils.getProgramSupplierName(this.supplierProductData[supplierId]?.supplier);
    },
    onProductChanged(period, dayIndex, itemIndex, v) {
      if (this.readonly) return;
      const newCosts = this.data.costs;
      const item = newCosts[dayIndex].items[itemIndex];
      item.product = v;

      const supplierId = item.supplier.value;
      if (supplierId == null) {
        return;
      }

      const productId = v.value;
      if (productId == null) {
        item.productCost = {
          type: "custom",
          currency: this.currentCurrency,
          value: {
            default: item.productCost.value.default,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        };
        item.isProductEditableCost = true;
        item.productProgramDescription = "";
        item.productProgramTitle = "";

        this.data = { ...this.data, costs: newCosts };

        return;
      }

      const product = this.supplierProductData[supplierId].products[productId];
      const previousDayHotelId = this.getPreviousDayHotelId(dayIndex);
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        dayIndex,
        previousDayHotelId
      );
      item.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      item.productType = product.product_type;
      item.costType = productCost.costType;
      this.applyProgramFields(item, product, supplierId, period);
      item.isProductEditableCost = false;
      quoteUtils.applyShowInProgramForProductType(item);

      this.data = { ...this.data, costs: newCosts };

      this.$emit("onProductTypeChanged", product.product_type);
    },
    onProductFocus(supplier) {
      this.$emit("onProductFocus", supplier.value);
    },
    onProductTypeChanged(dayIndex, itemIndex, v, supplier) {
      if (this.readonly) return;
      const newCosts = this.data.costs;
      const item = newCosts[dayIndex].items[itemIndex];
      item.product = null;
      item.productType = v;
      item.productProgramDescription = "";
      item.productProgramTitle = "";
      quoteUtils.applyShowInProgramForProductType(item);
      this.$emit("onProductTypeChanged", v);
      if (supplier != null) {
        this.$emit("onSupplierChanged", supplier.value, v);
      }
      this.data = { ...this.data, costs: newCosts };
    },
    onCostTypeChanged(dayIndex, itemIndex, v) {
      if (this.readonly) return;
      const newCosts = this.data.costs;
      const item = newCosts[dayIndex].items[itemIndex];
      item.costType = v;
      this.data = { ...this.data, costs: newCosts };
    },
    redirectToSupplier(supplier) {
      if (supplier?.value == null) return;
      window.open(this.$router.resolve("/suppliers/" + supplier.value).href, "_blank");
    },
    redirectToProduct(product) {
      if (product?.value == null) return;
      window.open(this.$router.resolve("/products/" + product.value).href, "_blank");
    },
    isSeasonalCost(productCostType) {
      if (this.data.period.startDay) return false;
      return productCostType === "seasonal";
    },
    getPreviousDayHotelId(dayIndex) {
      if (dayIndex === 0) return null;

      const perviousDayIndex = dayIndex - 1;
      const previousDayItems = this.data.costs[perviousDayIndex]?.items || [];
      if (previousDayItems.length === 0) return null;

      const room = previousDayItems.find((item) => item.productType === "ROOM");
      if (!room?.supplier?.value) return null;

      return room.supplier.value;
    },
    getPreviousDayAccommodationCity(dayIndex) {
      if (dayIndex === 0) return null;

      const previousDayItems = this.data.costs[dayIndex - 1]?.items || [];
      const room = previousDayItems.find((item) => item.productType === "ROOM");
      if (!room) return null;

      if (room.supplierCity) return room.supplierCity;

      const supplierId = room.supplier?.value;
      if (supplierId && this.supplierProductData[supplierId]?.supplier?.city) {
        return this.supplierProductData[supplierId].supplier.city;
      }

      return null;
    },
    showRefreshButton(dayIndex, itemIndex) {
      // return !this.data.costs?.[dayIndex]?.items?.[itemIndex]?.productCostType;
      return true;
    },
    refreshProductCostType(period, dayIndex, itemIndex) {
      if (this.readonly) return;
      const newCosts = this.data.costs;
      const item = newCosts[dayIndex].items[itemIndex];
      const isProductEditableCost = item.isProductEditableCost;

      if (isProductEditableCost) {
        item.productCostType = "custom";
        item.productCost.currency = this.currentCurrency;
        return;
      }

      const supplierId = item.supplier.value;
      const productId = item.product.value;

      if (!supplierId || !productId) return;

      const product = this.supplierProductData[supplierId].products[productId];
      const previousDayHotelId = this.getPreviousDayHotelId(dayIndex);
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        dayIndex,
        previousDayHotelId
      );
      item.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      item.productType = product.product_type;
      item.costType = productCost.costType;
      this.applyProgramFields(item, product, supplierId, period);
      quoteUtils.applyShowInProgramForProductType(item);

      this.data = { ...this.data, costs: newCosts };
    },
    fetchCities() {
      return this.$store
        .dispatch("DatabaseStore/getCityList")
        .then((cities) => {
          this.cities = cities || [];
        })
        .catch((err) => this.$root.$error(err));
    },
    normalizeCityValue(city) {
      if (city == null) return "";
      if (typeof city === "object") return String(city.value || city.label || "").trim();
      return String(city).trim();
    },
    hasCityValue(city) {
      return !!this.normalizeCityValue(city);
    },
    isAirportCity(city) {
      return /^aéroport de\s+/i.test(this.normalizeCityValue(city));
    },
    toAirportCity(city) {
      const name = this.normalizeCityValue(city);
      if (!name || this.isAirportCity(name)) return name;
      return `Aéroport de ${name}`;
    },
    getCityItems(day) {
      const selected = [day?.cityStart, day?.cityEnd].map(this.normalizeCityValue).filter(Boolean);
      return [...new Set([...(this.cities || []), ...selected])].sort((a, b) =>
        `${a}`.localeCompare(`${b}`, undefined, { sensitivity: "base" })
      );
    },
    syncCitiesFromItems(dayIndex) {
      if (this.readonly) return;
      const costs = [...this.data.costs];
      const day = costs[dayIndex];
      const { items } = day;
      const previousDayAccommodationCity = this.getPreviousDayAccommodationCity(dayIndex);
      const isFirstDay = dayIndex === 0;
      const isLastDay = costs.length > 0 && dayIndex === costs.length - 1;

      if (isFirstDay) {
        const arrivalCity = this.normalizeCityValue(this.data.cityStart);
        day.cityStart = arrivalCity ? this.toAirportCity(arrivalCity) : items[0]?.supplierCity ?? "";
      } else {
        day.cityStart = previousDayAccommodationCity ?? items[0]?.supplierCity ?? "";
      }

      if (isLastDay) {
        const departureCity = this.normalizeCityValue(this.data.cityEnd);
        day.cityEnd = departureCity
          ? this.toAirportCity(departureCity)
          : items[items.length - 1]?.supplierCity ?? "";
      } else {
        day.cityEnd = items[items.length - 1]?.supplierCity ?? "";
      }

      this.data = { ...this.data, costs };
    },
    onCityStartChanged(day, value) {
      if (this.readonly) return;
      const next = typeof value === "object" ? value?.value || value?.label || "" : value;
      const normalized = String(next || "").trim();
      if (day.cityStart === normalized) return;
      day.cityStart = normalized;
      this.resetRouteAndImage(day);
    },
    onCityEndChanged(day, value) {
      if (this.readonly) return;
      const next = typeof value === "object" ? value?.value || value?.label || "" : value;
      const normalized = String(next || "").trim();
      if (day.cityEnd === normalized) return;
      day.cityEnd = normalized;
      this.resetRouteAndImage(day);
    },
    resetRouteAndImage(day) {
      if (this.readonly) return;
      day.routeId = null;
      day.imageId = null;
      this.data = { ...this.data };
    },
    openRouteDialog(dayIndex) {
      if (this.readonly) return;
      const day = this.data.costs[dayIndex];
      const cityStart = typeof day.cityStart === "object" ? day.cityStart?.value || day.cityStart?.label || "" : day.cityStart;
      const cityEnd = typeof day.cityEnd === "object" ? day.cityEnd?.value || day.cityEnd?.label || "" : day.cityEnd;
      if (!cityStart || !cityEnd) {
        this.$root.$error("Please select a start and end city before choosing a route");
        return;
      }
      // Keep day cities as plain strings so route lookup stays consistent.
      day.cityStart = String(cityStart).trim();
      day.cityEnd = String(cityEnd).trim();
      this.routeDialog = {
        cityStart: day.cityStart,
        cityEnd: day.cityEnd,
        routeId: day.routeId,
        dayIndex,
      };
    },
    onRouteSelect(route) {
      if (!this.routeDialog) return;
      const costs = [...this.data.costs];
      const day = costs[this.routeDialog.dayIndex];
      day.routeId = route.id;
      this.$set(this.selectedRoutesById, route.id, {
        ...route,
        vias: normalizeRouteVias(route.vias),
      });
      this.data = { ...this.data, costs };

      const existingRoutes = this.$store.getters["QuoteStore/routes"] || [];
      if (!existingRoutes.find((item) => item && item.id === route.id)) {
        this.$store.commit("QuoteStore/SET_ROUTES", [...existingRoutes, route]);
      }
    },
    clearRoute(dayIndex) {
      if (this.readonly) return;
      const costs = [...this.data.costs];
      costs[dayIndex].routeId = null;
      this.data = { ...this.data, costs };
    },
    getSelectedRouteLabel(routeId) {
      const route = this.selectedRoutesById[routeId];
      if (!route) return "";
      const vias = normalizeRouteVias(route.vias);
      return vias.join(" / ");
    },
    prefetchSelectedRoutes() {
      const routeIds = [...new Set((this.data.costs || []).map((day) => day.routeId).filter(Boolean))];
      routeIds.forEach((routeId) => {
        if (this.selectedRoutesById[routeId]) return;
        this.$store
          .dispatch("RouteStore/fetchRouteById", routeId)
          .then((route) => {
            if (!route) return;
            this.$set(this.selectedRoutesById, routeId, {
              ...route,
              vias: normalizeRouteVias(route.vias),
            });
          })
          .catch(() => {});
      });
    },
  },
  watch: {
    "data.costs": {
      handler() {
        this.prefetchSelectedRoutes();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.fetchCities();
  },
};
</script>

<style scoped>
.quote-costs-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.quote-day-block {
  display: block;
  width: 100%;
  min-width: 720px;
  max-width: 100%;
  margin-top: 20px;
  padding: 0;
  border: 1px solid #bdbdbd;
  border-radius: 10px;
  background: #f8f9fa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  overflow: hidden;
}

.quote-day-block:first-of-type {
  margin-top: 12px;
}

.quote-day-block--alt {
  background: #fff;
}

.quote-day-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 0;
  padding: 10px 14px;
  background: #e8edf2;
  border-bottom: 1px solid #cfd8dc;
}

.quote-day-block--alt .quote-day-header {
  background: #f0f4f8;
}

.quote-day-reorder {
  display: inline-flex;
  align-items: center;
}

.quote-day-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  margin-left: 4px;
  color: #263238;
}

.quote-day-route-bar {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  width: auto;
  margin: 8px 14px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-sizing: border-box;
}

.quote-day-block--alt .quote-day-route-bar {
  background: #fafafa;
}

.quote-day-field {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1 1 0;
  min-width: 0;
}

.quote-day-field--via {
  flex: 1.15 1 0;
}

.quote-day-field--action {
  flex: 0 0 auto;
  justify-content: center;
}

.quote-day-field-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.quote-day-field-row > *:first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.quote-day-connector {
  align-self: center;
  margin-bottom: 0;
  color: #9e9e9e;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  flex: 0 0 auto;
}

.quote-day-sync {
  flex: 0 0 auto;
}

.quote-day-route-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 28px !important;
  min-height: 28px !important;
  padding: 0 10px !important;
  white-space: nowrap;
  margin-bottom: 0;
}

.quote-day-route-btn >>> .v-icon {
  margin-right: 4px !important;
}

.quote-day-field >>> .v-input,
.quote-day-field >>> .v-text-field {
  width: 100%;
}

.quote-day-field >>> .select-wrapper {
  width: 100%;
}

.quote-day-field--filled >>> .v-select__selection,
.quote-day-field--filled >>> .v-select__selections input,
.quote-day-field--filled >>> input {
  font-weight: 700 !important;
}

.quote-day-meals {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-sizing: border-box;
  flex: 0 0 auto;
}

.quote-day-block--alt .quote-day-meals {
  background: transparent;
}

.quote-day-meals-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  line-height: 1.2;
  white-space: nowrap;
}

.quote-day-meals-options {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0 8px;
}

.quote-day-meal-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0;
}

.quote-day-meal-letter {
  font-size: 12px;
  font-weight: 600;
  color: #455a64;
  line-height: 1;
  cursor: pointer;
  user-select: none;
}

.quote-day-meal-check {
  margin: 0 !important;
  padding: 0 !important;
  pointer-events: auto;
  position: relative;
  z-index: 1;
}

.quote-day-meal-check >>> .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

.quote-day-meal-check >>> .v-input__slot {
  margin: 0;
}

.quote-day-meal-check >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.quote-day-meals-info {
  flex: 0 0 auto;
  margin-left: 2px;
}

.quote-day-products {
  overflow-x: auto;
  width: auto;
  margin: 0 14px 10px;
  padding: 4px 8px 2px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-sizing: border-box;
}

.quote-day-block--alt .quote-day-products {
  background: #fafafa;
}

.quote-day-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
}

.quote-day-table th {
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
  line-height: 1.2;
  vertical-align: middle;
}

.quote-day-table td {
  padding: 4px 6px;
  vertical-align: middle;
  border-bottom: 1px solid #efefef;
}

.quote-day-table__row:hover td {
  background: #fafafa;
}

.quote-cost-field--missing >>> .v-text-field--outlined fieldset {
  border-color: #e53935 !important;
}

.quote-product-type-row td:first-child {
  box-shadow: inset 4px 0 0 var(--quote-product-type-accent);
}

.quote-day-table__empty-row td {
  height: 40px;
  padding: 10px 8px;
  border-bottom: 1px solid #efefef;
}

.quote-day-empty-label {
  font-size: 12px;
  color: #9e9e9e;
}

.quote-day-table__reorder {
  width: 56px;
  white-space: nowrap;
}

.quote-day-table__icon-cell {
  width: 28px;
  padding-left: 2px !important;
  padding-right: 2px !important;
  text-align: center;
}

.quote-day-table__program {
  text-align: center;
  min-width: 110px;
  vertical-align: middle;
  white-space: nowrap;
}

.quote-program-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 24px;
}

.quote-program-toggle >>> .v-input--selection-controls,
.quote-program-toggle >>> .v-input--switch {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.quote-program-toggle >>> .v-input--switch {
  transform: scale(0.82);
  transform-origin: center center;
}

.quote-program-toggle >>> .v-input__slot {
  margin: 0;
}

.quote-day-table__actions {
  width: 36px;
}

.quote-day-table__footer td {
  border-bottom: none;
  padding-top: 4px;
  padding-bottom: 2px;
}

.quote-day-inline-field {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 160px;
}

.quote-day-type-field {
  width: 170px;
}

.quote-day-cost-type-field {
  width: 130px;
}

.quote-day-warning-icon {
  height: 14px;
  width: 14px;
}

.quote-day-add-line {
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding-left: 0 !important;
}
</style>
